/**
 * AdminSessionWarning — sticky banner that appears when the admin's
 * hard-TTL session is within 15 minutes of expiry.
 *
 * The banner escalates visually:
 *   900 s (15 min)  — amber  — "Session expires in X min" + "Extend" button
 *   300 s ( 5 min)  — red    — urgent, "Save your work now"
 *
 * Clicking "Extend session" calls POST /admin/refresh to push the hard
 * ceiling forward by 12 hours — no re-login needed.
 * Clicking "Re-login" clears auth state so the login form reappears.
 */
import React, { useEffect, useState } from 'react';
import { adminAuth, adminApi } from '@/lib/api';
import { toast } from 'sonner';

const WARN_SECS  = 900; // 15 min — start showing banner
const URGENT_SECS = 300; // 5 min — escalate to red

export default function AdminSessionWarning({ onRelogin }) {
  const [secsLeft, setSecsLeft]   = useState(null);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    const check = () => {
      const s = adminAuth.secondsLeft;
      setSecsLeft(s);
    };
    check();
    const id = setInterval(check, 30_000); // recheck every 30 s
    return () => clearInterval(id);
  }, []);

  // Don't render if no expiry stored, or plenty of time left
  if (secsLeft === null || secsLeft > WARN_SECS) return null;

  const mins   = Math.ceil(secsLeft / 60);
  const urgent = secsLeft <= URGENT_SECS;

  const handleExtend = async () => {
    if (extending) return;
    setExtending(true);
    try {
      const r = await adminApi.refreshSession();
      toast.success(`Session extended by ${r.extended_by_hours || 12} hours.`);
      // Re-read the updated expiry from localStorage
      setSecsLeft(adminAuth.secondsLeft);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not extend session — please re-login.');
    } finally {
      setExtending(false);
    }
  };

  return (
    <div
      data-testid="admin-session-expiry-banner"
      className={`fixed top-0 left-0 right-0 z-[9999] flex items-center gap-3 px-4 py-2 text-sm font-semibold shadow-lg transition-colors ${
        urgent
          ? 'bg-red-600 text-white'
          : 'bg-amber-400 text-amber-950'
      }`}
    >
      <span className="flex-1">
        {urgent
          ? `Session expires in under ${mins} minute${mins !== 1 ? 's' : ''}. Save your work immediately.`
          : `Admin session expires in ${mins} minute${mins !== 1 ? 's' : ''}. Unsaved form entries will be lost.`}
      </span>
      <button
        data-testid="admin-session-extend-btn"
        onClick={handleExtend}
        disabled={extending}
        className={`shrink-0 rounded px-3 py-1 text-xs font-bold border transition-opacity hover:opacity-80 disabled:opacity-50 ${
          urgent
            ? 'border-white/70 text-white'
            : 'border-amber-800/50 text-amber-900'
        }`}
      >
        {extending ? 'Extending…' : 'Extend session'}
      </button>
      <button
        data-testid="admin-session-relogin-btn"
        onClick={onRelogin}
        className={`shrink-0 rounded px-3 py-1 text-xs font-bold border transition-opacity hover:opacity-80 ${
          urgent
            ? 'border-white/70 text-white'
            : 'border-amber-800/50 text-amber-900'
        }`}
      >
        Re-login now
      </button>
    </div>
  );
}
